import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queueApi } from "@bw/shared/api";
import type { PartyInviteResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/** The accept/decline payload — the invite id + the boolean decision. */
export interface RespondPartyInviteVars {
  inviteId: string;
  facilityId: string;
  accept: boolean;
}

/**
 * Accept or decline a party invite (POST /queue/party-invites/{id}/respond).
 *
 * On settlement the whole `party` arm is invalidated so the responded invite
 * leaves incoming lists and an acceptance updates party detail. The
 * facility-scoped current-party and membership inventory are invalidated
 * directly as well, so this client does not depend on SSE delivery to converge.
 * Rejected stale responses reconcile through the same path. The
 * mutation does NOT swallow errors — a 409 (already responded / party not open /
 * party full) or 403 (not the invitee) surfaces on `mutation.error` for the page
 * to map to honest inline copy.
 */
export function useRespondPartyInvite() {
  const queryClient = useQueryClient();

  return useMutation<PartyInviteResponse, unknown, RespondPartyInviteVars>({
    mutationFn: ({ inviteId, accept }: RespondPartyInviteVars) =>
      queueApi.respondPartyInvite(inviteId, accept),
    onSettled: (_data, _error, { facilityId }) => {
      // Prefix-invalidate every party detail slot — an accept changes a roster.
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.party.all,
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.dropin.myParty(facilityId),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.dropin.partyMemberships(facilityId),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.dropin.session(facilityId),
          refetchType: "active",
        }),
      ]);
    },
  });
}
